import React, {useCallback, useMemo} from 'react';
import parse from 'html-react-parser';
import Button from 'js/components/Common/Button/Button';
import Heading from 'js/components/Common/Heading/Heading';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';
import Thumbnail from 'js/components/Common/Thumbnail/Thumbnail';
import useFetchCallback from 'js/hooks/useFetchCallback/useFetchCallback';

import classes from '../Items.module.pcss';
import {CommonContentItemType} from '../DataTypes';
import {downloadManualFetchData} from '../../fetchData';
import ProductLabelsList from 'js/components/Common/ProductLabelList/ProductLabelsList';
import ProductContentService from 'js/components/Taxonomies/ProductContent/ProductContentService';
import VisibilityViewerIcon from 'js/components/Common/VisibilityViewerIcon/VisibilityViewerIcon';
import {ContentType} from 'js/components/Taxonomies/ProductContent/ProductContent';

interface Props {
    pageTitle: string,
    activeItem: CommonContentItemType,
    isFocus?: boolean,
    parent?: number,
    activeTab?:ContentType
}

const Item = ({pageTitle, activeItem, isFocus = false, parent}: Props) => {
    const {databaseId, link, title, uri, type, buttonLabel, description} = activeItem || {};

    const getEventData = useCallback(value =>
        ProductContentService.getMixPanelEventData(pageTitle, value, parent)
        , [pageTitle, parent]);

    const itemEventClickHandler = useCallback(async (val: string) => {
        await MixPanelWrapper.TrackEvent(
            'CTA button is clicked on. Content section',
            getEventData(val)
        );
    }, [getEventData]);

    const fileDownloadHandler = useFetchCallback({
        updateData: data => data?.manualsSingle?.updateDownloads,
        query: () => `${downloadManualFetchData(databaseId)}`,
        onStart: () => {
            const element = document.createElement('a');
            element.setAttribute('href', link);
            const ext = link.split('.').pop();
            element.setAttribute('download', `${parse(title)}.${ext}`);

            element.style.display = 'block';
            element.click();
        },
        onSuccess: async () => {
            await MixPanelWrapper.TrackEvent(
                'Download Manual',
                getEventData(title)
            );
        },
        onFailure: () => {
            console.error('Error while downloading');
        },
        onError: (e) => {
            console.error(e);
        }
    });

    const buttonType = useMemo(() => {
        switch (type) {
            case 'articles' : return 'ArticleButton';
            case 'manuals' : return 'ManualButton';
            case 'videos' : return 'VideoButton';
            case 'courses' : return 'CourseButton';
            default : return 'ButtonColored';
        }
    }, [type]);

    const button = useMemo(() => {
        if (type === 'manuals') {
            return <Button
                additionalStylesClass={classes.Button}
                type={'button'}
                buttonType={buttonType}
                handler={fileDownloadHandler}
                text={isFocus ? buttonLabel : ''} />;
        }
        return <Button
            additionalStylesClass={classes.Button}
            type={'a'}
            handler={() => itemEventClickHandler(title)}
            buttonType={buttonType}
            link={`/${uri}`}
            text={isFocus ? buttonLabel : ''} />;
    }, [type, buttonType, uri, isFocus, buttonLabel, fileDownloadHandler, itemEventClickHandler, title]);

    return (
        <div key={activeItem?.databaseId} className={`${classes.Item} ${isFocus && classes.OnFocusItem}`}>
            <div className={classes.Top}>
                <Thumbnail
                    type={activeItem?.type}
                    additionalClass={isFocus && classes.OnFocusLabel}
                    alt={title} sizeType={isFocus ? 'L' : 'S'} />
                <div className={classes.Info}>
                    <Heading text={`${title}`} type={'h4'}
                     insideEl={<VisibilityViewerIcon
                        id={activeItem?.databaseId}
                     />}/>
                    <ProductLabelsList pageId={activeItem?.databaseId} />
                    <div className={classes.Description}>{parse(description || '')}</div>
                </div>
            </div>
            {button}
        </div>
    );
};

export default Item;
